import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Alert, {
  getFriendlyErrorMessage,
  getNotificationForError,
  success as successNotificationType,
  error as errorNotificationType,
} from "../../Alert";
import AdminGrid, { direction } from "../../AdminGrid/AdminGrid";
import { columnTypes } from "../../AdminGrid/AdminGrid";
import { useHistory } from "react-router-dom";
import { deleteUrl, get } from "../../../Common/api";
import { setCurrentPageNotification } from "../../../redux/notificationSlice";
import getActions from "../../../Common/actions";
import DeleteMenuLayoutDialog from "./DeleteMenuLayoutDialog";
import { orderBy } from "@progress/kendo-data-query";
import "./CustomMenuLayoutList.css";

export default function MenuLayoutList() {
  // history represents the browser history and allows us to navigate to other pages
  const history = useHistory();
  // Used to dispatch Redux messages which we mainly use for alert messages
  const dispatch = useDispatch();
  // A place to store the menu layouts loaded from the server
  const [menuLayouts, setMenuLayouts] = useState([]);
  // A place to store available actions loaded from the server
  const [availableActions, setavailableActions] = useState([]);
  // A variable to control whether the delete dialog is visible
  const [isDeleteDialogDialogVisible, setIsDeleteDialogDialogVisible] =
    useState(false);
  // A place to store how many terminals are assigned to a given layout when
  // we try to delete it
  const [assignedPosTerminalsCount, setAssignedPosTerminalsCount] = useState(0);
  // The layout we are trying to delete
  const [selectedLayoutToDelete, setSelectedLayoutToDelete] = useState({});

  // Get available actions from server based on permissions
  const actions = getActions();

  // A place to store current sorting state
  const [sort, setSort] = useState([{ field: "title", dir: direction.ASC }]);

  // Determine which actions are available
  const canEdit =
    availableActions.filter((a) => a.name === actions.update.name).length > 0;
  const canCreate =
    availableActions.filter((a) => a.name === actions.create.name).length > 0;
  const canDelete =
    availableActions.filter((a) => a.name === actions.delete.name).length > 0;
    
  // Get actions for each data item in the grid
  // In this case, the data item is a menu layout
  const getActionsForLayout = (layout) => {
    // The array of actions available for this item
    const myActions = [];
    
    // The edit action for this item (if available)
    const editAction = {
      title: "Edit",
      label: `Edit layout ${layout.title}`,
      // When this action is clicked, we will navigate to /menulayouts/:id
      onClick: (props) => history.push(`/menulayouts/${props.dataItem.id}`),
    };
    
    // The assign action for this item (if available)
    const assignAction = {
      title: "Assign",
      label: `Assign layout ${layout.title}`,
      // When this action is clicked, we will navigate to /menulayouts/:id/assignments
      onClick: (props) =>
        history.push(`/menulayouts/${props.dataItem.id}/assignments`),
    };
    
    // The delete action for this item (if available)
    const deleteAction = {
      title: "Delete",
      label: `Delete layout ${layout.title}`,
      // When this action is clicked, we call the canManuLayoutDelete [sic] function
      onClick: (props) => canManuLayoutDelete(props.dataItem),
    };
    
    // Add availabe actions for this item based on permissions
    if (canEdit) {
      myActions.push(assignAction);
      myActions.push(editAction);
    }
    if (canDelete) {
      myActions.push(deleteAction);
    }
    
    // Return available actions for this item
    return myActions;
  };
  
  // Construct the list of data items
  // Order by current sort order stored in "sort"
  const listData = orderBy(
    menuLayouts.map((layout) => ({
      ...layout,
      // Add an "actions" property for each item containing
      // an array of available actions
      actions: getActionsForLayout(layout),
    })),
    sort
  );

  // Event handler for when sorting changes in the grid
  const sortChange = (event) => {
    // Do not sort by actions column
    if(event.sort.length > 0 && event.sort[event.sort.length - 1].field === 'actions') {
      return;
    }
    setSort(event.sort);
  };

  // Load data when component mounts
  useEffect(() => {
    // Scroll to the top of the page when component mounts so
    // we can see the alert banner for example after saving
    // a new menu layout
    window.scrollTo(0, 0);
    
    // Promise.all is used to wait for multiple server calls to return
    Promise.all([
      // Get the list of menu layouts
      get("/MenuLayouts").catch((error) => {
        // If there is an error, dispatch the error message for it
        dispatch(setCurrentPageNotification(getNotificationForError(error)));
      }),
      // Get the list of actions based on user permissions
      get("/MenuLayouts/Actions").catch((error) => {
        // If there is an error, dispatch the error message for it
        dispatch(setCurrentPageNotification(getNotificationForError(error)));
      }),
    ]).then((values) => {
      // At this point, both server calls have returned successfully
      // Assign the result of the first server call (list of menu layouts)
      // to a constant
      const layouts = values[0];
      if (layouts) {
        // Set layouts in state
        setMenuLayouts(layouts);
      }
      // The actions returned from the server
      const actions = values[1];
      if (actions) {
        // Set actions in state
        setavailableActions(actions);
      }
    });
  },
  // We're supposed to pass in an array of values that the useEffect depends on
  // The useEffect callback gets called whenever these values change.
  // Technically, we only want it to get called once when the component mounts
  // so we could just pass in an empty array, but React will give us a warning
  // because it sees that we are referencing the dispatch function which was
  // declared outside this module. The dispatch function will never change
  // so adding it here has the same effect as passing in an empty array.
  // We are only doing it to get rid of the warning.
  [dispatch]);

  // This is the function that gets called when you click the "delete"
  // action. It just checks whether there are terminals assigned to the
  // layout and displays the appropriate dialog based on that.
  const canManuLayoutDelete = async (menuLayout) => {
    try {
      const posTerminalsCount = await get(
        `/MenuLayouts/${menuLayout.id}/CanDelete`,
        "Failed to check for pos assignment"
      );
      setAssignedPosTerminalsCount(posTerminalsCount);
      setSelectedLayoutToDelete(menuLayout);
      setIsDeleteDialogDialogVisible(true);
    } catch (error) {
      dispatch(setCurrentPageNotification(getNotificationForError(error)));
    }
  };

  // Click handler for cancel button of delete dialog
  const onCancel = async () => {
    setIsDeleteDialogDialogVisible(false);
  };

  // Click handler of delete button on delete dialog
  const onDelete = async (menuLayout) => {
    try {
      await deleteUrl(
        `/MenuLayouts/${menuLayout.id}`,
        "Failed to delete the menu layout"
      );
      const menuLayoutsFiltered = menuLayouts.filter(
        (m) => m.id != menuLayout.id
      );
      setMenuLayouts(menuLayoutsFiltered);
      dispatch(
        setCurrentPageNotification({
          type: successNotificationType,
          message: `Menu Layout ${menuLayout.title} has been deleted.`,
        })
      );
    } catch (error) {
      dispatch(setCurrentPageNotification(getNotificationForError(error)));
    }
    setIsDeleteDialogDialogVisible(false);
  };

  // Column definition for the grid
  const columns = [
    {
      field: "title",
      title: "Name",
    },
    // Actions column definition
    {
      field: "actions",
      title: "Actions",
      type: columnTypes.actions,
    },
  ];
  
  // Return the JSX (i.e. the markup) for the component.
  return (
    <React.Fragment>
      { /* The alert dialog. The alert message gets dispatched to Redux */ }
      <Alert />
      { /* The delete dialog which only displays if isDeleteDialogDialogVisible [sic] is true */ }
      {isDeleteDialogDialogVisible && (
        <DeleteMenuLayoutDialog
          assignedPosTerminalsCount={assignedPosTerminalsCount}
          menuLayout={selectedLayoutToDelete}
          onCancel={onCancel}
          onDelete={onDelete}
        ></DeleteMenuLayoutDialog>
      )}
      { /* Bread crumb */ }
      <div className="row">
        <div className="col-md-12">
          <h2>Menu Layouts</h2>
          <ul className="page-breadcrumb breadcrumb">
            <li>
              <i className="fa fa-user fa-fw"></i>
              <Link to="/menulayouts">Menu</Link>
              <i className="fa fa-angle-right"></i>
            </li>
            <li>
              <Link to="/menulayouts">Menu Layouts</Link>
            </li>
          </ul>
        </div>
      </div>
      { /* The create button which only displays if you have permission to create */ }
      {canCreate && (
        <div className="row MarginBottomDiv">
          <div className="col-md-12 ">
            <div className="btn-group pull-left">
              <Link to="/menulayouts/new" className="ActionLink">
                <div id="sub-div">
                  <button
                    id="AddNewButton"
                    className="btn green"
                    style={{ width: "200px", marginBottom: "20px" }}
                  >
                    Add New Menu Layout
                  </button>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}
      { /* The Admin Grid */ }
      <AdminGrid
        columns={columns}
        data={listData}
        sortable={{ allowUnsort: false }}
        sort={sort}
        sortChange={sortChange}
      />
    </React.Fragment>
  );
}
